import { AuthTokenResponse, GoogleJwtPayload, Subscription, UserPreferences, UserProfile } from 'src/types/interfaces';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useContext } from "react";
import { CredentialResponse } from "@react-oauth/google";
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { redirect } from 'react-router-dom';
import { APIClient } from '../lib/api_client';
import Cookies from "js-cookie";


interface AuthContextType {
  handleRegistrationSubmit: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  handleLoginSubmit: (email: string, password: string) => Promise<void>;
  handleSocialAuthTokenExchange: (response: CredentialResponse) => Promise<void>;
  logOut: () => void;
  getAuthTokenClaims: () => JwtPayload;
  subscriptions: Subscription[] | [];
  userProfile: UserProfile | null;
  refreshAccessToken: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  updateProfile: (profileData: Partial<UserProfile>) => Promise<UserProfile>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<UserPreferences>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  handleRegistrationSubmit: async () => { },
  handleLoginSubmit: async () => { },
  handleSocialAuthTokenExchange: async () => { },
  logOut: () => { },
  deleteAccount: async () => { },
  getAuthTokenClaims: () => { return {} },
  refreshAccessToken: () => { return {} },
  subscriptions: [],
  userProfile: null,
  isAuthenticated: false,
  isLoading: false,
  updateProfile: async () => { throw new Error('Not implemented') },
  updatePreferences: async () => { throw new Error('Not implemented') }
});

/**
 * React component that provides authentication context for the application.
 * It manages authentication state, token handling, and related operations.
 */
const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();

  const getAuthTokenClaims = (): JwtPayload => {
    const token = Cookies.get("mindara_at");
    if (token) {
      return jwtDecode(token)
    }
    return {}
  }

  const exchangeSocialToken = async (socialCredential: string): Promise<AuthTokenResponse> => {
    const response = await APIClient.post<AuthTokenResponse>('/token/social/', { socialCredential: socialCredential });
    return response.data;
  };

  const refreshAccessToken = async (): Promise<AuthTokenResponse> => {
    const refreshToken = Cookies.get("mindara_rt");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await APIClient.post<AuthTokenResponse>('/token/refresh/', { refresh: refreshToken });
    const { access, refresh } = response.data;

    Cookies.set("mindara_at", access, { sameSite: "lax", expires: 1 });
    Cookies.set("mindara_rt", refresh, { sameSite: "lax", expires: 7 });

    return response.data;
  };

  const verifyToken = async (): Promise<boolean> => {
    try {
      let token = Cookies.get("mindara_at");
      if (!token) return false;

      const decodedToken = jwtDecode<GoogleJwtPayload | undefined>(token);
      if (decodedToken === undefined) {
        console.debug("No authentication token exists");
        return false;
      }

      // If token is expired but we have a refresh token, try to refresh
      if (decodedToken.exp < (Date.now() / 1000)) {
        console.debug("Access token expired, attempting refresh");
        try {
          await refreshAccessToken();
          token = Cookies.get("mindara_at");
          if (!token) return false;
        } catch (refreshError) {
          console.debug("Token refresh failed");
          return false;
        }
      }

      const response = await APIClient.post('/token/verify/', { token });
      return response.status === 200;
    } catch {
      return false;
    }
  };

  const tokenVerificationQuery = useQuery({
    queryKey: ['tokenVerify'],
    queryFn: verifyToken,
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: false
  });

  const subscriptionsQuery = useQuery({
    queryKey: ['subscriptions'],
    queryFn: async () => {
      const response = await APIClient.get('/subscriptions/');
      return response?.data?.results || [];
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    enabled: tokenVerificationQuery.data ?? false,
    retry: false
  });

  const profileQuery = useQuery({
    queryKey: ['userProfile'],
    queryFn: async (): Promise<UserProfile> => {
      const response = await APIClient.get('/profile/');
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    enabled: tokenVerificationQuery.data ?? false,
    retry: false
  });

  const handleRegistrationSubmit = async (email: string, password: string, firstName: string, lastName: string) => {
    const response = await APIClient.post('/users/', { email: email.toLowerCase(), password, first_name: firstName, last_name: lastName });
    return response.data;
  }

  const handleLoginSubmit = async (email: string, password: string) => {
    const response = await APIClient.post('/token/', { username: email.toLowerCase(), password });
    if (response.status === 200) {
      const tokens = response.data;
      Cookies.set("mindara_at", tokens.access, { sameSite: "lax", expires: 1 });
      Cookies.set("mindara_rt", tokens.refresh, { sameSite: "lax", expires: 7 });

      // Invalidate token verification query to force a refresh
      await queryClient.invalidateQueries({ queryKey: ['tokenVerify'] });
      await queryClient.invalidateQueries({ queryKey: ['userPlans'] });
      await queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    } else {
      console.error("Login failed:", response.data);
    }
  }

  const handleSocialAuthTokenExchange = async (response: CredentialResponse) => {
    if (!response.credential) {
      throw new Error("No credential received from Google");
    }

    try {
      const tokens = await exchangeSocialToken(response.credential);
      Cookies.set("social_at", response.credential, { sameSite: "lax", expires: 7 });
      Cookies.set("mindara_at", tokens.access, { sameSite: "lax", expires: 1 });
      Cookies.set("mindara_rt", tokens.refresh, { sameSite: "lax", expires: 7 });

      // Invalidate token verification query to force a refresh
      await queryClient.invalidateQueries({ queryKey: ['tokenVerify'] });
      await queryClient.invalidateQueries({ queryKey: ['userPlans'] });
      await queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    } catch (error) {
      console.error('Token exchange failed:', error);
      throw error;
    }
  };

  const updateProfile = async (profileData: Partial<UserProfile>): Promise<UserProfile> => {
    // Get the current user's ID from the profile
    const currentProfile = queryClient.getQueryData<UserProfile>(['userProfile']);
    if (!currentProfile?.id) {
      throw new Error('User profile not loaded');
    }

    const response = await APIClient.patch(`/profile/${currentProfile.id}/`, profileData);

    // Update the cache with the full response data
    queryClient.setQueryData(['userProfile'], (oldData: UserProfile | undefined) => {
      if (!oldData) return response.data;

      // Merge the response data with old data to ensure all fields are updated
      return {
        ...oldData,
        ...response.data,
        person: {
          ...oldData.person,
          ...(response.data.person || {}),
        },
      };
    });

    // Invalidate the query to ensure fresh data on next fetch
    queryClient.invalidateQueries({ queryKey: ['userProfile'] });

    return response.data;
  };

  const updatePreferences = async (preferences: Partial<UserPreferences>): Promise<UserPreferences> => {
    const response = await APIClient.patch('/profile/preferences/', preferences);

    // Update the profile cache with new preferences
    queryClient.setQueryData(['userProfile'], (oldData: UserProfile | undefined) => {
      if (oldData) {
        return {
          ...oldData,
          preferences: response.data
        };
      }
      return oldData;
    });

    return response.data;
  };

  const logOut = () => {
    Cookies.remove("social_at");
    Cookies.remove("mindara_at");
    Cookies.remove("mindara_rt");
    queryClient.invalidateQueries({ queryKey: ['tokenVerify'] });
    queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    redirect("/login")
  };

  const deleteAccount = async () => {
    try {
      const refreshToken = Cookies.get("mindara_rt");
      await APIClient.post('/users/me/delete/', {
        refresh_token: refreshToken,
      });
    } finally {
      Cookies.remove("social_at");
      Cookies.remove("mindara_at");
      Cookies.remove("mindara_rt");
      queryClient.invalidateQueries({ queryKey: ['tokenVerify'] });
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      redirect("/login");
    }
  };

  const value = {
    handleRegistrationSubmit,
    handleLoginSubmit,
    handleSocialAuthTokenExchange,
    logOut,
    deleteAccount,
    getAuthTokenClaims,
    refreshAccessToken,
    updateProfile,
    updatePreferences,
    isAuthenticated: tokenVerificationQuery.data ?? false,
    isLoading: tokenVerificationQuery.isLoading || (tokenVerificationQuery.data && (subscriptionsQuery.isLoading || profileQuery.isLoading)) || false,
    subscriptions: subscriptionsQuery.data || [],
    userProfile: profileQuery.data || null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext, AuthProvider, useAuth };
